import { Html, Text } from "@react-three/drei";
import { cn, formatNumber } from "@tosize/ui";
import { useIntl } from "react-intl";
import { DoubleSide } from "three";
import { P, match } from "ts-pattern";
import { useCabinetStore } from "../../../context/StoreContext";
import { useCanvasStore } from "../../../hooks/useCanvasStore";
import { useDimensions } from "../hooks/useDimensions";

interface SizeProps {
	size: number;
	variant?: "height" | "width" | "depth" | "default";
	position: { x: number; y: number; z: number };
}

export default function SizePill(props: SizeProps) {
	const { size, variant = "default" } = props;
	const { showDimensions, fullScreen } = useCanvasStore();
	const { format } = useDimensions();
	const intl = useIntl();

	const rotation = match(variant)
		.with("height", () => ({ x: 0, y: 0, z: -Math.PI / 2 }))
		.with("depth", () => ({ x: 0, y: fullScreen ? 0 : -Math.PI / 2, z: 0 }))
		.otherwise(() => undefined);

	const position = {
		x: rotation?.y ? props.position.x - 0.001 : props.position.x,
		y: props.position.y,
		z: rotation?.y ? props.position.z : props.position.z + 0.001,
	};

	if (fullScreen) {
		return <CanvasSizePill {...props} rotation={rotation} position={position} />;
	}

	return (
		<Html
			position={[position.x, position.y, position.z]}
			rotation={rotation ? [rotation.x, rotation.y, rotation.z] : undefined}
			pointerEvents="none"
			sprite={showDimensions}
			className={cn(
				"pointer-events-none select-none whitespace-nowrap rounded-full border px-2 text-xs",
				{
					"border-shades-medium bg-shades-medium text-white":
						variant === "height" || variant === "default",
					"border-shades-dark bg-shades-dark text-white": variant === "width",
					"border-shades-medium-light bg-shades-medium-light text-dark": variant === "depth",
				},
			)}
			center
		>
			<div className="text-xs">
				{variant === "depth"
					? formatNumber({
							lang: intl.locale,
							value: size / 10,
							unit: "centimeter",
							digits: { min: 0 },
						})
					: format(size)}
			</div>
		</Html>
	);
}

function CanvasSizePill(props: SizeProps & { rotation?: { x: number; y: number; z: number } }) {
	const { size, variant, position, rotation } = props;
	const { state } = useCabinetStore();

	const width = state.width / 1000;
	const sizeLength = size.toString().length;

	return (
		<mesh
			position={[position.x, position.y, position.z]}
			rotation={rotation ? [rotation.x, rotation.y, rotation.z] : undefined}
		>
			<planeGeometry
				args={match(width)
					.returnType<[number, number]>()
					.with(P.number.gt(4), () => [sizeLength * 0.04, 0.065])
					.with(P.number.gt(3), () => [sizeLength * 0.035, 0.055])
					.with(P.number.gt(2), () => [sizeLength * 0.03, 0.045])
					.otherwise(() => [sizeLength * 0.025, 0.035])}
			/>

			<meshBasicMaterial
				side={DoubleSide}
				color={match(variant)
					.with("height", () => "#979797")
					.with("width", () => "#F4F4F4")
					.with("depth", () => "#979797")
					.otherwise(() => "#6C6C6C")}
			/>

			<Text
				position={[0, 0, 0.001]}
				fontSize={match(width)
					.with(P.number.gt(4), () => 0.06)
					.with(P.number.gt(3), () => 0.05)
					.with(P.number.gt(2), () => 0.04)
					.otherwise(() => 0.03)}
				color="black"
				anchorX="center"
				anchorY="middle"
			>
				{size}
			</Text>
		</mesh>
	);
}
